<script>
  import {onMount, createEventDispatcher} from 'svelte';
  import {parse, stringify} from 'query-string';
  import {Button} from '@px/button';
  import {PATHS} from '@/const/paths';
  import {IP_RESTRICTED_CODE} from '@/emum';
  import {PATTERNS} from '@/const/patterns';
  import {drawerUtil} from '@/helper/drawer';
  import {userApi} from '@/api/user';
  import {fixLink$} from '@/store/lang';
  import {mc} from '@/core';
  import EmailInput from '@/widgets/EmailInput.svelte';
  import PasswordInput from '@/widgets/PasswordInput.svelte';
  import errors from '@/modules/error/error.lang';
  import lang from '../BindGoogleAccount.lang';

  export let LG = null;
  export let verificationData = null;
  export let oauthData = null;

  const dispatch = createEventDispatcher();

  let innerWidth = 1200;
  let target = '';
  let email = '';
  let password = '';
  let totpRequired = '';
  let code = '';
  let isSeeCode = false;
  let isShowError = false;
  let replyData = {};
  let isOauth = false;
  let search = null;

  $: errorPasswordMsg = validatePassword(password);

  onMount(() => {
    const query = parse(window.location.search);
    target = query.target;
    email = verificationData.email;
    code = verificationData.code;
    totpRequired = verificationData.totpRequired;
    isOauth = oauthData.isOauth;
    search = oauthData.search;
  });

  function validatePassword(value) {
    if (value === '') {
      return LG(lang.NoPasswordError);
    }
    if (value && !PATTERNS.PASSWORD.test(value)) {
      return LG(lang.PasswordError);
    }
    return null;
  }

  function handleEnterKey(e) {
    if (e.keyCode === 13) {
      handleConfirmClick();
    }
  }

  async function handleConfirmClick() {
    isShowError = false;
    if (totpRequired) {
      drawerUtil.launchGoogleVerificationPage(LG, {
        context: {email, code, password},
        async callback(googleCode, callback, context) {
          const data = await confirmLoginSure(googleCode);
          callback(data);
        },
      });
      return;
    }
    const params = {email, code, password};
    const {data, error, reply} = await userApi.confirmAuthLogin(params);
    if (data) {
      if (isOauth) {
        window.location.assign($fixLink$(PATHS.OAUTH_AUTHORIZE.pathname) + search + '&status=logged');
        return;
      }
      setTimeout(() => {
        window.location.assign(PATHS.HOME.pathname);
      }, 200);
    }
    if (error) {
      if ([IP_RESTRICTED_CODE.RESTRICTED, IP_RESTRICTED_CODE.WARNING].includes(error.code)) {
        drawerUtil.launchRegisterTips(LG);
        return;
      }
      replyData = reply;
      if (error.code === 34005) {
        isShowError = true;
      }
      mc.send('toast', 'error', {message: LG(lang.InvalidPasswordOrGoogle)});
    }
  }

  async function confirmLoginSure(otpCode) {
    isShowError = false;
    const params = {email, code, password, totpCode: otpCode};
    const {data, error, reply} = await userApi.confirmAuthLogin(params);
    if (data) {
      if (isOauth) {
        window.location.assign($fixLink$(PATHS.OAUTH_AUTHORIZE.pathname) + search + '&status=logged');
        return {code: 0, msg: ''};
      }
      setTimeout(() => {
        window.location.assign(PATHS.HOME.pathname);
      }, 200);
      return {code: 0, msg: ''};
    }
    if (error) {
      if ([IP_RESTRICTED_CODE.RESTRICTED, IP_RESTRICTED_CODE.WARNING].includes(error.code)) {
        drawerUtil.launchRegisterTips(LG);
        return;
      }
      replyData = reply;
      if (error.code === 34005) {
        isShowError = true;
      }
      mc.send('toast', 'error', {message: LG(lang.InvalidPasswordOrGoogle)});
      return {code: error.code, msg: LG(lang.InvalidPasswordOrGoogle)};
    }
  }

  function checkTarget(target) {
    if (!target) {
      return false;
    }

    if (target.indexOf('@') > -1) {
      return false;
    }

    if (/^https?:/.test(target)) {
      const targetUrl = new URL(target);
      return targetUrl.host === location.host;
    }

    return true;
  }
</script>

<style>
  .warp {
    background: #fff;
  }
  .code :global(.digit-box) {
    margin: 0;
    border: 1px solid #dbdfe7 !important;
    border-radius: 8px;
    width: 60px;
    height: 72px;
  }
  .code :global(.digit-box span) {
    line-height: 72px;
    font-weight: var(--fw3);
    font-size: 36px;
    color: var(--blue-main);
  }
  .code :global(.digit-box input) {
    padding: 0;
    width: 1px;
  }
  .code :global(.digit-box explain) {
    margin: 6px 0;
  }
  .home-login-email :global(.item:hover) {
    border: 1px solid #e9ecef;
  }
  .btn {
    margin-top: 24px;
  }
  .btn :global(Button) {
    height: 48px;
  }
  .main :global(.item.br48),
  .main :global(button.br48) {
    border-radius: 48px;
  }
  @media screen and (min-width: 640px) {
    .modal-wrap .content {
      width: auto;
    }
    .modal-wrap .title {
      position: relative;
      left: -32px;
      text-align: left;
    }
    .modal-wrap .main {
      padding: 0;
    }
    .main {
      border-radius: 8px;
      width: 420px;
      background: #fff;
    }
    .content {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 0 auto;
      height: 100%;
    }
  }
  @media screen and (max-width: 640px) {
    .title2 {
      display: none;
    }
    .title {
      text-align: left;
      font-size: 24px;
    }
    .warp {
      background: #fff;
    }
    .content {
      padding: 0;
    }
    .main {
      margin: 0;
      padding: 24px 20px 40px;
    }
  }
</style>

<svelte:window bind:innerWidth />
<div class="warp mt24">
  <div class="content df fdc">
    <div class="main">
      <div class="title f24 lh36 fw3 T1">{LG(lang.BindGoogleAccount)}</div>
      <div class="title2 mt20 mb20 lh16 T4  f12">
        {LG(lang.AfterBindYour)}
      </div>
      <div class="mt24 ml4 lh16 f12 T3">{LG(lang.Email)}</div>
      <div class="home-login-email mt4">
        <EmailInput class="br48" bind:value={email} disabled />
      </div>
      <div class="mt24 ml4 lh16 f12 T3">{LG(lang.Password)}</div>
      <div class="home-login-password mt4">
        <PasswordInput class="br48" showIcon={false} autocomplete="new-password" placeholder={LG(lang.Password)} bind:value={password} on:keypress={handleEnterKey} />
      </div>
      {#if isShowError}
        <div class="mt12 f12 sell">{@html LG(errors[34005], {Times: replyData ? replyData.data.remainTimes : 0})}</div>
      {/if}
      <div>

      </div>
      <div class="mt12 ml4 lh16 f12 T3">{LG(lang.PleaseInputYour)}</div>
      <div class="df aic jcsb mt12 lh16 f12 fw2 cp ml4">
        <a class="forgot" href={$fixLink$(PATHS.RESET_PASSWORD.pathname) + (target ? '?' + stringify({target}) : '')} target="_self">{LG(lang.ForgotPassword)}</a>
      </div>
      <div class="btn">
        <Button class="wp100 br48" type="primary" newRadius on:click={handleConfirmClick} disabled={errorPasswordMsg}>
          {LG(lang.Bind)}
        </Button>
      </div>
    </div>
  </div>
</div>
